import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  Briefcase, Users, Bell, Menu, CalendarDays,
  Sun, Moon, UserCheck, RotateCw,User,
  ImageIcon, Video, PenIcon, LogOut, Settings, Tag, IndianRupee, Check, Clock, Filter, X
} from 'lucide-react';

import taskService from '../../api/taskService';
import research_rex_logo from '../../assets/research_rex (1).webp'; 

const NO_CAPTION_TEXT = "No Caption";

// Sunday-aware urgency logic
const WORK_START = 9.5;
const WORK_END = 18.5;
const DAILY_HOURS = WORK_END - WORK_START;

const getBusinessHoursDiff = (dueDateStr) => {
  const now = new Date();
  const due = new Date(dueDateStr);
  
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  if (dueDay < today) return { hours: 0, isOverdue: true };

  let totalWorkMinutes = 0;
  let current = new Date(now);

  while (current < due) {
    const day = current.getDay();
    const hour = current.getHours() + current.getMinutes() / 60;
    if (day !== 0) { // Sunday check intact
      if (hour >= WORK_START && hour < WORK_END) {
        totalWorkMinutes++;
      }
    }
    current.setMinutes(current.getMinutes() + 1);
  }

  return { hours: totalWorkMinutes / 60, isOverdue: false };
};

const getTimeRemaining = (dueDateStr) => {
  const now = new Date();
  const due = new Date(dueDateStr);
  
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  
  const diffTime = dueDay - today;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  const formattedDate = due.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });

  if (diffDays < 0) return `Overdue (${formattedDate})`;
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === 2) return `Day after Tomorrow (${formattedDate})`;
  
  // Sunday-aware work days calculation
  const { hours } = getBusinessHoursDiff(dueDateStr);
  const workDays = Math.floor(hours / DAILY_HOURS);
  
  if (workDays > 0) {
    return `${workDays} Work Day${workDays > 1 ? 's' : ''} (${formattedDate})`;
  }
  return formattedDate;
};

const HeaderLink = ({ label, icon, to, isDark }) => (
  <NavLink 
    to={to}
    className={({ isActive }) => {
      const activeColor = isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]';
      const inactiveColor = isDark ? 'text-slate-400' : 'text-slate-600';
      
      // Removed hover:bg and orange text. Added 'group' for sub-element animation.
      return `flex items-center gap-2 cursor-pointer transition-all relative py-2 px-1 group ${
        isActive ? activeColor : inactiveColor
      }`;
    }}
  >
    {({ isActive }) => (
      <>
        {React.cloneElement(icon, { size: 18, className: "transition-transform group-hover:-translate-y-0.5" })}
        <span className="text-[13px] font-detail uppercase tracking-brand hidden sm:inline transition-colors">
          {label}
        </span>

        {/* INDICATOR LINE CONTAINER */}
        <div className="absolute -bottom-[21px] left-0 right-0 flex justify-center">
          {/* Active Line (Always visible when active) */}
          {isActive ? (
            <div className={`w-full h-1 rounded-t-full transition-all duration-300 ${
              isDark ? 'bg-[#5A9AFF]' : 'bg-[#2D4D94]'
            }`}></div>
          ) : (
            /* Hover Line (Grows from center on hover) */
            <div className={`w-0 h-1 rounded-t-full bg-slate-300 transition-all duration-300 ease-out group-hover:w-full opacity-0 group-hover:opacity-100`}></div>
          )}
        </div>
      </>
    )}
  </NavLink>
);

const Header = ({ setIsSidebarOpen, isDark, setIsDark, onLogout, onTaskClick }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState('normal');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const settingsRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();

  const storedUser = JSON.parse(localStorage.getItem('user')) || {};
const displayName = storedUser.full_name || storedUser.name || "System User";
  const displayRole = storedUser.role || "Staff";
  const isAdmin = displayRole.toLowerCase() === 'admin';

  // REPLACE 'initials' logic with this:
  const formattedDisplayName = displayName.split(' ').map((part, index, array) => (
    <React.Fragment key={index}>
      <span className="text-orange-500">{part[0]}</span>
      {part.slice(1)}{index < array.length - 1 ? ' ' : ''}
    </React.Fragment>
  ));

  const fetchNotifications = async () => {
    setIsRefreshing(true);
    try {
      const data = await taskService.getNotifications();
      setNotifications(data);
    } catch (err) { 
      setNotifications([]); 
    } finally {
      setTimeout(() => setIsRefreshing(false), 600);
    }
  };

  const handleLogout = () => {
    onLogout(); 
    navigate('/login');
  };

const getUrgencyConfig = (dueDate) => {
  const now = new Date();
  const dueObj = new Date(dueDate);
  
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(dueObj.getFullYear(), dueObj.getMonth(), dueObj.getDate());
  
  const diffTime = dueDay - today;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  const dayName = dueObj.toLocaleDateString('en-US', { weekday: 'short' });
  const dateNum = dueObj.getDate();
  const monthName = dueObj.toLocaleDateString('en-US', { month: 'short' });

  // 1. RED: Before today
  if (diffDays < 0) {
    return { 
      color: 'text-red-500', 
      label: `Overdue (${dateNum} ${monthName})`, 
      bg: 'bg-red-500/10' 
    };
  }
  
  // 2. ORANGE: Today or Tomorrow
  if (diffDays <= 1) {
    const label = diffDays === 0 ? "Due Today" : "Due Tomorrow";
    return { color: 'text-orange-500', label: label, bg: 'bg-orange-500/10' };
  }
  
  // 3. BLUE: Day after tomorrow (Shows specific date)
  if (diffDays === 2) {
    return { 
      color: 'text-sky-500', 
      label: `Due ${dayName}, ${dateNum} ${monthName}`, 
      bg: 'bg-sky-500/10' 
    };
  }

  // 4. GREEN: 3+ days away
  return { 
    color: 'text-emerald-500', 
    label: `Upcoming (${dateNum} ${monthName})`, 
    bg: 'bg-emerald-500/10' 
  };
};

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) setShowNotifications(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false);
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettingsMenu(false);
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) setShowMobileMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalNotifications = notifications.filter(n => !n.confirmed_at);
  const queueNotifications = notifications.filter(n => n.confirmed_at && !n.posted_at);
  const activeList = activeTab === 'normal' ? normalNotifications : queueNotifications;

  return (
    <header className={`h-20 flex items-center justify-between px-6 md:px-12 relative z-[140] border-b lg:border-none transition-all duration-300 ${
      isDark ? 'bg-surface-dark border-slate-800' : 'bg-white border-slate-100'
    }`}>
      
      {/* LOGO AREA */}
      <div className="flex lg:hidden items-center ">
         {/* Insert your <img> here later */}
         <img src={research_rex_logo} alt="Logo" className='w-16'/>
         {/* <span className={`font-bold tracking-tighter text-xl ${isDark ? 'text-white' : 'text-slate-900'}`}>LOGO</span> */}
      </div>

      {/* RIGHT-ALIGNED WRAPPER: Contains Nav and Actions */}
      <div className="flex flex-1 items-center justify-end gap-2 md:gap-4 h-full">
        
        {/* DESKTOP NAV */}
        <nav className="hidden lg:flex items-center gap-8 h-full">
          <HeaderLink label="Schedule" to="/" isDark={isDark} icon={<CalendarDays />} />
          <HeaderLink label="Create" to="/create-task" isDark={isDark} icon={<PenIcon />} />
          <HeaderLink label="Brands" to="/brands" isDark={isDark} icon={<Briefcase />} />
          <HeaderLink label="Clients" to="/enquiry" isDark={isDark} icon={<UserCheck />} />
          {isAdmin && <HeaderLink label="Users" to="/users" isDark={isDark} icon={<Users />} />}
        </nav>

        {/* VERTICAL DIVIDER */}
        <div className={`hidden lg:block h-8 border-l ml-0 mr-0 ${
          isDark ? 'border-slate-800' : 'border-slate-300'
        }`} />

        {/* Desktop Theme Switcher */}
<button 
  onClick={() => setIsDark(!isDark)} 
  className={`hidden sm:flex p-2.5 rounded-xl transition-all duration-300 hover:text-orange-500 ${
    isDark ? 'hover:bg-slate-700 text-orange-400' : 'hover:bg-slate-200 text-slate-600'
  }`}
>
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* MOBILE FILTER (Moved here) */}
        <button 
          onClick={() => setIsSidebarOpen(true)} 
          className={`lg:hidden p-2.5 rounded-xl border transition-all active:scale-95 ${
            isDark ? 'text-slate-300 bg-slate-800 border-slate-700' : 'text-slate-600 bg-slate-50 border-slate-200'
          }`}
        >
          <Filter size={18} />
        </button>

        {/* Mobile Home/Schedule Link */}
        <button 
          onClick={() => navigate('/')}
          className={`lg:hidden p-2.5 rounded-xl border transition-all active:scale-95 ${
            isDark ? 'text-slate-300 bg-slate-800 border-slate-700' : 'text-slate-600 bg-slate-50 border-slate-200'
          }`}
        >
          <CalendarDays size={18} />
        </button>
        
        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
<button onClick={() => setShowNotifications(!showNotifications)} className={`p-2.5 lg:p-2 relative rounded-xl transition-all ${
  showNotifications 
    ? 'text-orange-500 bg-slate-500/10' 
    : (isDark ? 'text-slate-300 hover:bg-slate-500/10' : 'text-slate-600 hover:bg-slate-500/10')
}`}>            <Bell size={20} />
            {normalNotifications.length > 0 && <div className="absolute top-2.5 right-2.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-inherit animate-pulse"></div>}
          </button>

          {showNotifications && (
            /* FIX: fixed inset-x-4 on mobile prevents cut-off. md:absolute md:right-0 for desktop */
            <div className={`fixed inset-x-4 md:absolute md:inset-x-auto md:right-0 mt-4 w-auto md:w-[450px] rounded-2xl border shadow-2xl overflow-hidden z-[150] ${isDark ? 'bg-surface-dark border-slate-800' : 'bg-white border-slate-100'}`}>
              <div className={`flex border-b ${isDark ? 'border-slate-800 bg-slate-900/40' : 'bg-slate-50 border-slate-100'}`}>
                <button onClick={() => setActiveTab('normal')} className={`flex-1 py-4 text-[10px] font-bold uppercase tracking-widest transition-all ${activeTab === 'normal' ? 'text-indigo-500 border-b-2 border-indigo-500' : 'text-slate-400'}`}>Confirmation Queue ({normalNotifications.length})</button>
                <button onClick={() => setActiveTab('queue')} className={`flex-1 py-4 text-[10px] font-bold uppercase tracking-widest transition-all ${activeTab === 'queue' ? 'text-emerald-500 border-b-2 border-emerald-500' : 'text-slate-400'}`}>Post Queue ({queueNotifications.length})</button>
              </div>

              <div className="p-5 border-b border-inherit flex items-center justify-between bg-inherit sticky top-0 z-10">
                <div className="flex items-center gap-3">
                  <h4 className={`text-xs font-heading uppercase tracking-brand ${isDark ? 'text-white' : 'text-slate-900'}`}>{activeTab === 'normal' ? 'Alerts' : 'Post Queue'}</h4>
                  <button onClick={(e) => { e.stopPropagation(); fetchNotifications(); }} className={`p-1.5 rounded-lg hover:bg-slate-500/10 ${isRefreshing ? 'animate-spin' : ''}`}><RotateCw size={14} className="text-indigo-500" /></button>
                </div>
              </div>
              
              <div className="max-h-[420px] overflow-y-auto scrollbar-hide">
                {activeList.length === 0 ? (
                  <div className="p-10 text-center opacity-40 text-xs uppercase tracking-widest">No Tasks Found</div>
                ) : activeList.map((n) => {
                  const urgency = getUrgencyConfig(n.task_due_date);
                  return (
                    <div key={n.id} onClick={() => { onTaskClick(n); setShowNotifications(false); }} className={`p-5 border-b border-inherit flex gap-4 cursor-pointer transition-all active:scale-[0.98] ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'}`}>
                      <div className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center border-2 relative ${urgency.bg} ${urgency.color} border-current/20`}>
                        {n.type_name?.toLowerCase() === 'reel' ? <Video size={20} /> : <ImageIcon size={20} />}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <p className={`text-sm font-heading truncate mb-0.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>{n.post_title?.trim() || NO_CAPTION_TEXT}</p>
                        <p className={`text-xs font-detail uppercase tracking-wide ${isDark ? 'text-indigo-300' : 'text-brand-primary'}`}>{n.brand_name}</p>
                        <span className={`text-[10px] font-detail uppercase tracking-widest font-bold mt-2 ${urgency.color}`}>{getTimeRemaining(n.task_due_date)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Desktop Settings Menu */}
        <div className="hidden lg:relative lg:block" ref={settingsRef}>
<button 
  onClick={() => setShowSettingsMenu(!showSettingsMenu)} 
  className={`p-1 rounded-xl transition-all duration-100 hover:bg-slate-500/10 ${
    showSettingsMenu 
      ? (isDark ? 'bg-slate-500/20 text-orange-500' : 'bg-slate-500/10 text-orange-500') 
      : (isDark ? 'text-slate-300' : 'text-slate-600')
  }`}
>
 <Settings 
    size={20} 
    className={`transition-all duration-500 ease-in-out
      ${showSettingsMenu ? "rotate-90" : "rotate-0"}`} 
/>
</button>
          {showSettingsMenu && (
            <div className={`absolute right-0 mt-4 w-60 rounded-2xl border shadow-2xl overflow-hidden ${isDark ? 'bg-surface-dark border-slate-800' : 'bg-white border-slate-100'}`}>
              <div className="p-4 border-b border-inherit"><h4 className={`text-xs font-heading uppercase tracking-brand ${isDark ? 'text-white' : 'text-slate-900'}`}>Configuration</h4></div>
              <div className="p-2">
                <button onClick={() => { navigate('/settings/post-types'); setShowSettingsMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase transition-colors ${isDark ? 'text-slate-300 hover:bg-slate-800 hover:text-indigo-400' : 'text-slate-600 hover:bg-slate-50 hover:text-indigo-600'}`}><Tag size={16} /> Post Formats</button>
                <button onClick={() => { navigate('/settings/budget-ranges'); setShowSettingsMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase transition-colors ${isDark ? 'text-slate-300 hover:bg-slate-800 hover:text-indigo-400' : 'text-slate-600 hover:bg-slate-50 hover:text-indigo-600'}`}><IndianRupee size={16} /> Budget Ranges</button>
              </div>
            </div>
          )}
        </div>

{/* Profile / Desktop Avatar */}
        <div className="hidden lg:relative lg:block" ref={profileRef}>
          <div 
            onClick={() => setShowProfileMenu(!showProfileMenu)} 
            className={`group flex items-center gap-3 cursor-pointer p-1 rounded-2xl transition-all ${
              showProfileMenu ? (isDark ? 'bg-slate-800' : 'bg-slate-50') : ''
            }`}
          >
            <div className="text-right pl-2">
              <div className={`text-xs font-heading leading-tight transition-colors group-hover:text-orange-500 ${
                isDark ? 'text-slate-300' : 'text-slate-600'
              }`}>
                {formattedDisplayName}
              </div>
              <div className={`text-[10px] font-detail opacity-60 ${
                isDark ? 'text-slate-400' : 'text-slate-500'
              }`}>
                {displayRole}
              </div>
            </div>
            
            {/* Icon Container: Slate BG, Blue Outline -> Orange Outline on Hover, No Shadow */}
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-300 
              ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <User 
                size={20} 
                className={`transition-colors duration-300 stroke-[2.5px] ${
                  isDark 
                    ? 'text-[#5A9AFF] group-hover:text-orange-500' 
                    : 'text-[#2D4D94] group-hover:text-orange-500'
                }`} 
              />
            </div>
          </div>

          {showProfileMenu && (
            <div className={`absolute right-0 mt-4 w-64 rounded-2xl border shadow-2xl overflow-hidden ${
              isDark ? 'bg-surface-dark border-slate-800' : 'bg-white border-slate-100'
            }`}>
              <div className="p-3">
                <div className={`px-4 py-4 mb-2 rounded-2xl ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                  <p className={`text-sm font-heading truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {displayName}
                  </p>
                </div>
                <button 
                  onClick={handleLogout} 
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase text-rose-500 hover:bg-rose-500/10 transition-all"
                >
                  <LogOut size={16} /> Logout System
                </button>
              </div>
            </div>
          )}
        </div>

        {/* MOBILE HAMBURGER MENU */}
        <div className="relative lg:hidden" ref={mobileMenuRef}>
          <button 
            onClick={() => setShowMobileMenu(!showMobileMenu)} 
            className={`p-2.5 rounded-xl border transition-all active:scale-95 ${
              isDark ? 'text-slate-300 bg-slate-800 border-slate-700' : 'text-slate-600 bg-slate-50 border-slate-200'
            }`}
          >
            {showMobileMenu ? <X size={18} /> : <Menu size={18} />}
          </button>

          {showMobileMenu && (
            <div className={`absolute right-0 mt-4 w-72 rounded-2xl border shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200 ${
              isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'
            }`}>
              <div className={`p-4 border-b ${isDark ? 'border-slate-800 bg-slate-800/40' : 'bg-slate-50 border-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-heading ${isDark ? 'bg-indigo-500 text-white shadow-indigo-500/20' : 'bg-slate-900 text-white'}`}>{formattedDisplayName}</div>
                    <div className="min-w-0">
                        <p className={`text-xs font-heading truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{displayName}</p>
                        <p className={`text-[10px] font-detail opacity-60 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{displayRole}</p>
                    </div>
                  </div>
              </div>

              <div className="p-2 space-y-1">
                <p className="px-4 py-2 text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">Menu</p>
                <button onClick={() => { navigate('/create-task'); setShowMobileMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}><PenIcon size={16} /> Create Task</button>
                <button onClick={() => { navigate('/brands'); setShowMobileMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}><Briefcase size={16} /> Brands</button>
                <button onClick={() => { navigate('/enquiry'); setShowMobileMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}><UserCheck size={16} /> Clients</button>
                {isAdmin && <button onClick={() => { navigate('/users'); setShowMobileMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}><Users size={16} />Users</button>}
                
                <div className={`my-2 border-t ${isDark ? 'border-slate-800' : 'border-slate-100'}`} />
                
                <p className="px-4 py-2 text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">Settings</p>
                <button onClick={() => { navigate('/settings/post-types'); setShowMobileMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}><Tag size={16} /> Post Formats</button>
                <button onClick={() => { navigate('/settings/budget-ranges'); setShowMobileMenu(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}><IndianRupee size={16} /> Budget Ranges</button>
                
                <button onClick={() => setIsDark(!isDark)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase ${isDark ? 'text-yellow-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}>
                  {isDark ? <Sun size={16} /> : <Moon size={16} />} 
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </button>

                <div className={`my-2 border-t ${isDark ? 'border-slate-800' : 'border-slate-100'}`} />

                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-heading uppercase text-rose-500 hover:bg-rose-500/10 transition-all"><LogOut size={16} /> Logout</button>
              </div>
            </div>
          )}
        </div>

      </div>
    </header>
  );
};

export default Header;
